import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Grid, Alert, Snackbar, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import MicIcon from '@mui/icons-material/Mic';

const Contact = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [contactFormData, setContactFormData] = useState({ subject: '', message: '' });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContactChange = (e) => {
    setContactFormData({ ...contactFormData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Google Sheets Web App URL
    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwx6ueakF4wmzg2TKaJZeT1em2tXrqWHqoNopMHuwoP60dNPA5YNexEi61An0T6dL00kA/exec';
    
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      setSnackbarMessage('Successfully subscribed! 🎸');
      setOpenSnackbar(true);
      setFormData({ name: '', email: '' });
    } catch (error) {
      setSnackbarMessage('Something went wrong. Please try again.');
      setOpenSnackbar(true);
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const mailtoLink = `mailto:boxrooster@gmail.com?subject=${encodeURIComponent(contactFormData.subject)}&body=${encodeURIComponent(contactFormData.message)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show success message and clear form
    setSnackbarMessage('Opening your email client... 📧');
    setOpenSnackbar(true);
    setContactFormData({ subject: '', message: '' });
  };

  const actionButtons = [
    // { icon: <EmailIcon />, label: 'Management', email: 'management@boxrooster.com' },
    { icon: <MicIcon />, label: 'Booking', email: 'jason@soundtalentgroup.com' },
  ];

  return (
    <Box
      id="contact"
      ref={ref}
      sx={{
        minHeight: '100vh',
        py: 10,
        backgroundColor: '#0a0a0a',
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '4rem' },
              color: '#FFFFFF',
            }}
          >
            CONNECT
          </Typography>
          <Box
            sx={{
              width: '100px',
              height: '4px',
              backgroundColor: theme.palette.primary.main,
              margin: '0 auto 6rem',
            }}
          />
        </motion.div>

        {/* Newsletter Form */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              backgroundColor: '#121212',
              p: 4,
              borderRadius: 2,
              border: `2px solid ${theme.palette.primary.main}`,
              mb: 6,
            }}
          >
            <Typography variant="h5" sx={{ color: '#FFFFFF', mb: 1, textAlign: 'center' }}>
              Subscribe to Boxrooster
            </Typography>
            <Typography sx={{ color: '#AAA', mb: 4, textAlign: 'center' }}>
              Stay up to date with shows, new releases, and exclusive content
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  name="name"
                  label="Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      '& fieldset': { borderColor: '#555' },
                      '&:hover fieldset': { borderColor: theme.palette.primary.light },
                      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#AAA',
                      fontFamily: 'Inter, sans-serif',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      '& fieldset': { borderColor: '#555' },
                      '&:hover fieldset': { borderColor: theme.palette.primary.light },
                      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#AAA',
                      fontFamily: 'Inter, sans-serif',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    height: '56px',
                    backgroundColor: theme.palette.primary.main,
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    },
                  }}
                >
                  SUBSCRIBE
                </Button>
              </Grid>
            </Grid>
          </Box>
        </motion.div>

        {/* Contact Us Form */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <Box
            component="form"
            onSubmit={handleContactSubmit}
            sx={{
              backgroundColor: '#121212',
              p: 4,
              borderRadius: 2,
              border: `2px solid ${theme.palette.primary.main}`,
              mb: 6,
            }}
          >
            <Typography variant="h5" sx={{ color: '#FFFFFF', mb: 1, textAlign: 'center' }}>
              Contact Us
            </Typography>
            <Typography sx={{ color: '#AAA', mb: 4, textAlign: 'center' }}>
              Send us a message and we'll get back to you
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="subject"
                  label="Subject"
                  value={contactFormData.subject}
                  onChange={handleContactChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      '& fieldset': { borderColor: '#555' },
                      '&:hover fieldset': { borderColor: theme.palette.primary.light },
                      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#AAA',
                      fontFamily: 'Inter, sans-serif',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="message"
                  label="Message"
                  multiline
                  rows={4}
                  value={contactFormData.message}
                  onChange={handleContactChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      '& fieldset': { borderColor: '#555' },
                      '&:hover fieldset': { borderColor: theme.palette.primary.light },
                      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#AAA',
                      fontFamily: 'Inter, sans-serif',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    py: 1.5,
                    backgroundColor: theme.palette.primary.main,
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    },
                  }}
                >
                  CONTACT
                </Button>
              </Grid>
            </Grid>
          </Box>
        </motion.div>

        {/* Contact Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              mb: 4,
              color: '#FFFFFF',
            }}
          >
            Get in Touch
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {actionButtons.map((button, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Button
                  component="a"
                  href={`mailto:${button.email}`}
                  fullWidth
                  startIcon={button.icon}
                  sx={{
                    py: 3,
                    backgroundColor: '#121212',
                    color: '#FFFFFF',
                    border: `2px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                      borderColor: theme.palette.primary.light,
                      transform: 'translateY(-5px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {button.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Snackbar for feedback */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity="success"
            sx={{ 
              backgroundColor: theme.palette.primary.main,
              color: '#FFFFFF' 
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Contact;
